/**
 * French business days calculator (jours ouvrés)
 * Mirrors backend/shared/business_days.mjs for frontend use.
 * Excludes weekends + 11 French public holidays.
 */

function toDateStr(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Get all French public holidays for a given year.
 * 8 fixed dates + 3 Easter-based (lundi de Pâques, Ascension, lundi de Pentecôte).
 */
export function getFrenchHolidays(year) {
  const holidays = new Set();

  // Fixed holidays
  holidays.add(`${year}-01-01`); // Jour de l'An
  holidays.add(`${year}-05-01`); // Fête du Travail
  holidays.add(`${year}-05-08`); // Victoire 1945
  holidays.add(`${year}-07-14`); // Fête nationale
  holidays.add(`${year}-08-15`); // Assomption
  holidays.add(`${year}-11-01`); // Toussaint
  holidays.add(`${year}-11-11`); // Armistice
  holidays.add(`${year}-12-25`); // Noël

  // Easter Sunday (Anonymous Gregorian algorithm)
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  const easter = new Date(year, month - 1, day);

  // Easter Monday (+1)
  const easterMon = new Date(easter);
  easterMon.setDate(easterMon.getDate() + 1);
  holidays.add(toDateStr(easterMon));

  // Ascension (+39)
  const ascension = new Date(easter);
  ascension.setDate(ascension.getDate() + 39);
  holidays.add(toDateStr(ascension));

  // Whit Monday (+50)
  const whitMon = new Date(easter);
  whitMon.setDate(whitMon.getDate() + 50);
  holidays.add(toDateStr(whitMon));

  return holidays;
}

// Cache holidays per year
const holidayCache = {};
function getHolidaysForYear(year) {
  if (!holidayCache[year]) {
    holidayCache[year] = getFrenchHolidays(year);
  }
  return holidayCache[year];
}

/**
 * Check if a date is a French business day (not weekend, not holiday).
 */
export function isBusinessDay(date) {
  const d = new Date(date);
  const day = d.getDay();
  if (day === 0 || day === 6) return false;
  const holidays = getHolidaysForYear(d.getFullYear());
  return !holidays.has(toDateStr(d));
}

/**
 * Count business days between two dates (exclusive of start, inclusive logic matches backend).
 */
export function businessDaysBetween(startDate, endDate) {
  const start = new Date(startDate);
  const end = new Date(endDate);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  if (end <= start) return 0;

  let count = 0;
  const current = new Date(start);
  while (current < end) {
    current.setDate(current.getDate() + 1);
    if (isBusinessDay(current)) count++;
  }
  return count;
}
